import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Seo } from "./Seo";
import PageHero from "./UI/PageHero";
import SectionHeading from "./UI/SectionHeading";
import Breadcrumbs, { breadcrumbSchema } from "./UI/Breadcrumbs";
import Faq, { faqSchema } from "./UI/Faq";
import CtaBand from "./UI/CtaBand";
import Icon from "./UI/Icon";
import { getArea } from "../data/areas";
import { getService } from "../data/services";
import { SITE } from "../data/site";

export default function AreaPage() {
  const { slug } = useParams();
  const area = getArea(slug);

  if (!area) return <Navigate to="/areas" replace />;

  const url = `${SITE.url}/areas/${area.slug}/`;
  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Areas We Cover", to: "/areas" },
    { label: area.name },
  ];

  const services = (area.popularServices || []).map(getService).filter(Boolean);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Carpentry & joinery in ${area.name}`,
    serviceType: "Carpentry and joinery",
    description: area.metaDescription,
    url,
    areaServed: { "@type": "City", name: area.name },
    provider: { "@id": `${SITE.url}/#business` },
  };

  return (
    <>
      <Seo
        title={area.metaTitle}
        description={area.metaDescription}
        canonical={url}
        type="article"
        schema={[serviceSchema, faqSchema(area.faqs), breadcrumbSchema(crumbs)]}
      />

      <PageHero
        eyebrow={area.eyebrow}
        title={area.heroTitle}
        intro={area.heroIntro}
        crumbs={<Breadcrumbs items={crumbs} />}
        actions={
          <>
            <Link to="/contact" className="btn-primary">Get a free quote</Link>
            <a href={SITE.phoneHref} className="btn-outline-light">
              <Icon name="phone" className="w-4 h-4" /> {SITE.phone}
            </a>
          </>
        }
      />

      {/* Overview */}
      <section className="max-w-site mx-auto px-5 sm:px-10 pt-20 pb-16 md:pt-28 md:pb-20">
        <div className="lg:grid lg:grid-cols-[1.6fr_1fr] lg:gap-16 items-start">
          <div className="prose-links">
            <SectionHeading eyebrow="Overview" title={`Your local carpenter in ${area.name}`} />
            <div className="mt-6 space-y-5 text-bark text-lg leading-8">
              {area.intro.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <p>
                Explore our full range of <Link to="/carpentry-services">carpentry services</Link>,
                see <Link to="/our-work">recent projects</Link>, or{" "}
                <Link to="/contact">request a free quote</Link>.
              </p>
            </div>
          </div>

          <aside className="mt-10 lg:mt-0 bg-linen border border-hairline p-7 lg:sticky lg:top-24">
            <h3 className="font-display font-semibold text-2xl text-ink">Serving {area.name}</h3>
            <p className="text-bark leading-7 mt-3">
              Free, no-obligation visits and quotes across {area.name} and the surrounding area.
            </p>
            <div className="mt-6 space-y-3">
              <a href={SITE.phoneHref} className="btn-dark w-full">
                <Icon name="phone" className="w-4 h-4" /> {SITE.phone}
              </a>
              <Link to="/contact" className="btn-outline w-full">Send us a message</Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Popular services in this area */}
      {services.length > 0 && (
        <section className="bg-linen border-y border-hairline">
          <div className="max-w-site mx-auto px-5 sm:px-10 py-16 md:py-20">
            <SectionHeading eyebrow="Popular here" title={`What we're asked for in ${area.name}`} center />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to={`/carpentry-services/${s.slug}`}
                  className="group bg-paper border border-hairline overflow-hidden hover:border-oak transition-colors"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.cardTitle}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-semibold text-xl text-ink group-hover:text-oak transition-colors">
                      {s.cardTitle}
                    </h3>
                    <p className="text-bark leading-7 mt-2">{s.cardBlurb}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Local FAQ */}
      <section className="max-w-site mx-auto px-5 sm:px-10 py-16 md:py-20">
        <div className="lg:grid lg:grid-cols-[1fr_1.6fr] lg:gap-16 items-start">
          <div className="mb-8 lg:mb-0">
            <SectionHeading eyebrow="Questions" title={`Carpentry in ${area.name}`} />
          </div>
          <Faq items={area.faqs} />
        </div>
      </section>

      <CtaBand
        title={`Need a carpenter in ${area.name}?`}
        text={`Tell us about your project and we'll arrange a free visit and quote across ${area.name} and the surrounding area.`}
      />
    </>
  );
}
